import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  InputAdornment,
  Breadcrumbs,
  Link as MuiLink,
  TablePagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';
import ClearIcon from '@mui/icons-material/Clear';

interface Borrower {
  id: string;
  shortName: string;
  fullName: string;
  address: string;
  manager: {
    name: string;
    phone: string;
    email: string;
  };
  contactPerson: {
    name: string;
    department: string;
    phone: string;
    email: string;
  };
  monthlyRevenueForecast: number;
  status: 'Active' | 'Inactive';
  creationDate: string;
  lastUpdated: string;
  createdBy: string;
  lastUpdatedBy: string;
}

const initialBorrowers: Borrower[] = [
  {
    id: 'BR001',
    shortName: 'ABC Corp',
    fullName: 'ABC Corporation Ltd',
    address: 'King Abdullah Road, Riyadh, Saudi Arabia',
    manager: {
      name: 'Fahad Al-Saud',
      phone: '+966-11-555-1234',
      email: 'fahad.s@abccorp.sa'
    },
    contactPerson: {
      name: 'Omar Khan',
      department: 'Finance',
      phone: '+966-11-555-5678',
      email: 'omar.k@abccorp.sa'
    },
    monthlyRevenueForecast: 5000000,
    status: 'Active',
    creationDate: '2024-01-01',
    lastUpdated: '2024-03-15',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  },
  {
    id: 'BR002',
    shortName: 'XYZ Ltd',
    fullName: 'XYZ Limited',
    address: 'Olaya Street, Riyadh',
    manager: {
      name: 'Sara Al-Fahad',
      phone: '+966-11-555-2222',
      email: 'sara.f@xyz.sa'
    },
    contactPerson: {
      name: 'Ali Hassan',
      department: 'Treasury',
      phone: '+966-11-555-3333',
      email: 'ali.h@xyz.sa'
    },
    monthlyRevenueForecast: 3000000,
    status: 'Inactive',
    creationDate: '2023-10-10',
    lastUpdated: '2024-02-10',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  },
  {
    id: 'BR003',
    shortName: 'MNO Inc',
    fullName: 'MNO Incorporated',
    address: 'Jeddah Corniche, Jeddah',
    manager: {
      name: 'Khalid Al-Mutairi',
      phone: '+966-12-555-4444',
      email: 'khalid.m@mno.sa'
    },
    contactPerson: {
      name: 'Fatima Noor',
      department: 'Finance',
      phone: '+966-12-555-5555',
      email: 'fatima.n@mno.sa'
    },
    monthlyRevenueForecast: 4200000,
    status: 'Active',
    creationDate: '2022-08-15',
    lastUpdated: '2024-01-20',
    createdBy: 'Treasury',
    lastUpdatedBy: 'Treasury'
  },
  {
    id: 'BR004',
    shortName: 'PQR Group',
    fullName: 'PQR Group Holdings',
    address: 'Dammam Industrial Area',
    manager: {
      name: 'Mona Al-Qahtani',
      phone: '+966-13-555-6666',
      email: 'mona.q@pqr.sa'
    },
    contactPerson: {
      name: 'Yousef Al-Harbi',
      department: 'Operations',
      phone: '+966-13-555-7777',
      email: 'yousef.h@pqr.sa'
    },
    monthlyRevenueForecast: 2500000,
    status: 'Active',
    creationDate: '2023-03-05',
    lastUpdated: '2024-03-01',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  },
  {
    id: 'BR005',
    shortName: 'STU LLC',
    fullName: 'STU Limited Liability Company',
    address: 'Al Khobar, Eastern Province',
    manager: {
      name: 'Majed Al-Dosari',
      phone: '+966-13-555-8888',
      email: 'majed.d@stu.sa'
    },
    contactPerson: {
      name: 'Layla Al-Saleh',
      department: 'Finance',
      phone: '+966-13-555-9999',
      email: 'layla.s@stu.sa'
    },
    monthlyRevenueForecast: 3700000,
    status: 'Inactive',
    creationDate: '2022-12-01',
    lastUpdated: '2023-12-15',
    createdBy: 'Treasury',
    lastUpdatedBy: 'Treasury'
  },
  {
    id: 'BR006',
    shortName: 'VWX Partners',
    fullName: 'VWX Partners Co.',
    address: 'King Fahd Road, Riyadh',
    manager: {
      name: 'Huda Al-Shehri',
      phone: '+966-11-555-0000',
      email: 'huda.s@vwx.sa'
    },
    contactPerson: {
      name: 'Salman Al-Otaibi',
      department: 'Legal',
      phone: '+966-11-555-1010',
      email: 'salman.o@vwx.sa'
    },
    monthlyRevenueForecast: 6100000,
    status: 'Active',
    creationDate: '2024-02-10',
    lastUpdated: '2024-03-10',
    createdBy: 'System Admin',
    lastUpdatedBy: 'System Admin'
  }
];

const BorrowerManagement: React.FC = () => {
  const [borrowers, setBorrowers] = useState<Borrower[]>(initialBorrowers);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBorrower, setEditingBorrower] = useState<Borrower | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState<Partial<Borrower>>({
    shortName: '',
    fullName: '',
    address: '',
    manager: {
      name: '',
      phone: '',
      email: ''
    },
    contactPerson: {
      name: '',
      department: '',
      phone: '',
      email: ''
    },
    monthlyRevenueForecast: 0,
    status: 'Active'
  });

  const handleOpenDialog = (borrower?: Borrower) => {
    if (borrower) {
      setEditingBorrower(borrower);
      setFormData(borrower);
    } else {
      setEditingBorrower(null);
      setFormData({
        shortName: '',
        fullName: '',
        address: '',
        manager: {
          name: '',
          phone: '',
          email: ''
        },
        contactPerson: {
          name: '',
          department: '',
          phone: '',
          email: ''
        },
        monthlyRevenueForecast: 0,
        status: 'Active'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBorrower(null);
  };

  const handleInputChange = (field: string, value: string | number) => {
    if (field.includes('.')) {
      const [section, subfield] = field.split('.');
      setFormData(prev => {
        const sectionData = prev[section as keyof typeof prev];
        if (typeof sectionData === 'object' && sectionData !== null) {
          return {
            ...prev,
            [section]: {
              ...sectionData,
              [subfield]: value
            }
          };
        }
        return prev;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = () => {
    if (editingBorrower) {
      // Update existing borrower
      setBorrowers(prev => prev.map(borrower => 
        borrower.id === editingBorrower.id 
          ? { 
              ...borrower, 
              ...formData, 
              lastUpdated: new Date().toISOString().split('T')[0],
              lastUpdatedBy: 'Current User'
            }
          : borrower
      ));
    } else {
      // Add new borrower
      const newBorrower: Borrower = {
        ...formData as Borrower,
        id: `BR${String(borrowers.length + 1).padStart(3, '0')}`,
        creationDate: new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString().split('T')[0],
        createdBy: 'Current User',
        lastUpdatedBy: 'Current User'
      };
      setBorrowers(prev => [...prev, newBorrower]);
    }
    handleCloseDialog();
  };

  const handleDelete = (borrowerId: string) => {
    if (window.confirm('Are you sure you want to delete this borrower?')) {
      setBorrowers(prev => prev.filter(borrower => borrower.id !== borrowerId));
    }
  };

  const filteredBorrowers = borrowers.filter(borrower => 
    borrower.shortName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    borrower.fullName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const paginatedBorrowers = filteredBorrowers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/parameters">
          Parameters
        </MuiLink>
        <Typography color="text.primary">Borrower Management</Typography>
      </Breadcrumbs>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ color: '#111827', fontWeight: 'bold', letterSpacing: '-0.025em' }}>
          Borrower Management
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              bgcolor: '#fff',
              color: '#1E293B',
              borderColor: '#E5E7EB',
              textTransform: 'none',
              boxShadow: 'none',
              px: 2.5,
              '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
            }}
          >
            Export to Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              bgcolor: '#fff',
              color: '#1E293B',
              borderColor: '#E5E7EB',
              textTransform: 'none',
              boxShadow: 'none',
              px: 2.5,
              '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
            }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            color="primary"
            sx={{
              fontWeight: 600,
              px: 4,
              borderRadius: 2,
              boxShadow: '0 1px 2px 0 rgba(59,130,246,0.08)',
              textTransform: 'none',
              '&:hover': { bgcolor: '#2563EB' }
            }}
          >
            Add New Borrower
          </Button>
        </Box>
      </Box>

      {/* Search and Filter */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: 3,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.03)',
        }}
      >
        <TextField
          placeholder="Search borrowers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280' }} />
              </InputAdornment>
            ),
            sx: { bgcolor: '#fff', borderRadius: 2, height: 40 }
          }}
          sx={{ minWidth: 240, maxWidth: 320 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          size="medium"
          sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<ClearIcon />}
          onClick={() => { setSearchTerm(''); }}
          size="medium"
          sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
        >
          Reset
        </Button>
      </Box>

      {/* Borrowers Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)', background: '#fff', mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Borrower ID</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Short Name</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Full Name</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Monthly Revenue</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedBorrowers.map((borrower, idx) => (
              <TableRow
                key={borrower.id}
                hover
                sx={{
                  '&:hover': { bgcolor: '#F5F6FF' },
                  borderBottom: idx !== paginatedBorrowers.length - 1 ? '1px solid #E5E7EB' : 'none',
                  transition: 'background 0.2s',
                }}
              >
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{borrower.id}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{borrower.shortName}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{borrower.fullName}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{formatCurrency(borrower.monthlyRevenueForecast)}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Chip 
                    label={borrower.status} 
                    size="small"
                    sx={{ 
                      bgcolor: borrower.status === 'Active' ? '#D1FAE5' : '#FEE2E2',
                      color: borrower.status === 'Active' ? '#059669' : '#DC2626',
                      fontWeight: 500
                    }} 
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Stack direction="row" spacing={1}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(borrower)}
                      sx={{ color: '#6366F1' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(borrower.id)}
                      sx={{ color: '#DC2626' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredBorrowers.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      {/* Add/Edit Borrower Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          {editingBorrower ? 'Edit Borrower' : 'Add New Borrower'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Basic Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Short Name"
                    value={formData.shortName}
                    onChange={(e) => handleInputChange('shortName', e.target.value)}
                    required
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    required
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    required
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Monthly Revenue Forecast"
                    value={formData.monthlyRevenueForecast}
                    onChange={(e) => handleInputChange('monthlyRevenueForecast', Number(e.target.value))}
                    required
                    type="number"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">SAR</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Manager Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Manager Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.manager?.name}
                    onChange={(e) => handleInputChange('manager.name', e.target.value)}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.manager?.phone}
                    onChange={(e) => handleInputChange('manager.phone', e.target.value)}
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={formData.manager?.email}
                    onChange={(e) => handleInputChange('manager.email', e.target.value)}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Contact Person Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Contact Person Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.contactPerson?.name}
                    onChange={(e) => handleInputChange('contactPerson.name', e.target.value)}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Department"
                    value={formData.contactPerson?.department}
                    onChange={(e) => handleInputChange('contactPerson.department', e.target.value)}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.contactPerson?.phone}
                    onChange={(e) => handleInputChange('contactPerson.phone', e.target.value)}
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={formData.contactPerson?.email}
                    onChange={(e) => handleInputChange('contactPerson.email', e.target.value)}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              color: '#374151',
              '&:hover': { bgcolor: '#F3F4F6' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              bgcolor: '#6366F1',
              '&:hover': { bgcolor: '#4F46E5' },
              textTransform: 'none',
              px: 3
            }}
          >
            {editingBorrower ? 'Update Borrower' : 'Add Borrower'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BorrowerManagement; 